/**
 * Finding sentence helpers — every phrase cites the numbers behind it.
 */

import {
  formatNumber,
  teamListLabel,
} from './fleetStats';
import type { Evidence, Finding, InsightSeverity } from './types';

export function evidenceChipLabel(ev: Evidence): string {
  const ours = formatNumber(ev.ours);
  const leaders = formatNumber(ev.leaders);
  const unit = ev.unit ? ` ${ev.unit}` : '';
  return `${ev.label}: ours ${ours}${unit} · leaders ${leaders}${unit}`;
}

export function sortFindings(findings: Finding[]): Finding[] {
  const severityOrder: Record<InsightSeverity, number> = {
    loss: 0,
    gain: 1,
    neutral: 2,
  };
  return [...findings].sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity]
  );
}

export function buildGapDetail(opts: {
  metricLabel: string;
  ours: number | null;
  leaders: number | null;
  unit: string;
  leaderTeams: string[];
  weAreWorse: boolean;
  betterIsHigher: boolean;
}): string {
  const { metricLabel, ours, leaders, unit, leaderTeams, weAreWorse, betterIsHigher } = opts;
  const u = unit ? ` ${unit}` : '';
  const leaderLabel = teamListLabel(leaderTeams);
  const oursStr = formatNumber(ours);
  const leadersStr = formatNumber(leaders);
  if (ours == null || leaders == null) {
    return `${metricLabel}: insufficient data to compare with ${leaderLabel}.`;
  }
  const delta = Math.abs(ours - leaders);
  const deltaStr = formatNumber(delta);
  if (weAreWorse) {
    const direction = betterIsHigher ? 'behind' : 'worse than';
    return `${metricLabel}: ${oursStr}${u} vs leaders (${leaderLabel}) ${leadersStr}${u} — ${deltaStr}${u} ${direction}.`;
  }
  const direction = betterIsHigher ? 'ahead of' : 'better than';
  return `${metricLabel}: ${oursStr}${u} vs leaders (${leaderLabel}) ${leadersStr}${u} — ${deltaStr}${u} ${direction} them.`;
}

export function buildRecommendation(opts: {
  template: string;
  leaderTeams: string[];
  metricLabel: string;
  leadersValue: number | null;
  unit: string;
}): string {
  const leadersStr = formatNumber(opts.leadersValue);
  const u = opts.unit ? ` ${opts.unit}` : '';
  return opts.template
    .replace(/\{leaders\}/g, teamListLabel(opts.leaderTeams))
    .replace(/\{metric\}/g, opts.metricLabel)
    .replace(/\{value\}/g, `${leadersStr}${u}`);
}
